import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import BarcodeScanner, { ScanButton } from "@/components/BarcodeScanner";
import { useAuth } from "@/context/AuthContext";
import { MedicineDetail } from "./MedicineDetail";
import { inr } from "@/lib/format";

const EMPTY = {
  code: "", name: "", generic_name: "", brand: "", category: "", dosage_form: "tablet", unit: "strip",
  strips_per_box: "0", tablets_per_strip: "0",
  barcode: "", hsn: "", mrp: "", purchase_price: "", selling_price: "", gst_percent: "12",
  low_stock_limit: "20", rack: "", supplier_ids: [],
};

export default function Medicines({ embedded = false }) {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [formError, setFormError] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);

  // Barcode scan: if a matching medicine exists, open it for edit; else prefill
  // the barcode into a new-medicine form so the user can complete it.
  const handleScan = useCallback(async (code) => {
    setScanOpen(false);
    try {
      const { data } = await api.get("/pharmacy/medicines/by-barcode", { params: { code } });
      if (data.medicine) {
        toast.success(`Found ${data.medicine.name} — opening for edit.`);
        edit(data.medicine);
      } else {
        toast.info(`Barcode ${code} is not registered. Enter product details.`);
        setEditing(null);
        setForm({ ...EMPTY, barcode: code });
        setOpen(true);
      }
    } catch (e) {
      toast.error(apiError(e) || "Barcode lookup failed.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setItems(null);
    try {
      if (q && q.trim().length >= 2) {
        // Query both tenant medicines and the global master catalog, merge results.
        const [tenantRes, masterRes] = await Promise.allSettled([
          api.get("/pharmacy/medicines", { params: { q: q || undefined, page, limit: 25 } }),
          api.get("/pharmacy/medicines/master/search", { params: { q: q } }),
        ]);
        const tenantItems = tenantRes.status === "fulfilled" ? (tenantRes.value.data.items || []) : [];
        const masterItems = masterRes.status === "fulfilled" ? (masterRes.value.data.items || []) : [];
        // Map tenant codes/names for dedupe
        const tenantKeys = new Set(tenantItems.map((t) => ((t.code || "") + "::" + (t.name || "")).toLowerCase()));
        const masters = masterItems.map((m) => ({ ...m, _is_master: true }));
        // Only include master items that are not already in tenant list (by code+name)
        const mastersFiltered = masters.filter((m) => {
          const k = ((m.code || "") + "::" + (m.name || "")).toLowerCase();
          return !tenantKeys.has(k);
        });
        const merged = [...tenantItems, ...mastersFiltered];
        setItems(merged);
        setTotal(tenantRes.status === "fulfilled" ? (tenantRes.value.data.total || merged.length) : merged.length);
      } else {
        const { data } = await api.get("/pharmacy/medicines", { params: { q: q || undefined, page, limit: 25 } });
        setItems(data.items); setTotal(data.total);
      }
    } catch (e) { setError(apiError(e)); }
  }, [q, page, setItems, setTotal, setError]);

  useEffect(() => {
    if (!can("pharmacy.medicines.view")) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, can]);

  useEffect(() => {
    if (!can("pharmacy.medicines.view")) return;
    api.get("/pharmacy/suppliers", { params: { status: "active" } })
      .then(({ data }) => setSuppliers(data.items)).catch(() => {});
  }, [can, setSuppliers]);

  if (!can("pharmacy.medicines.view")) return <NoAccess what="the medicine master" />;

  const numeric = (f) => ({
    ...f, mrp: Number(f.mrp || 0), purchase_price: Number(f.purchase_price || 0),
    selling_price: Number(f.selling_price || 0), gst_percent: Number(f.gst_percent || 0),
    low_stock_limit: Number(f.low_stock_limit || 0), supplier_ids: f.supplier_ids || [],
    strips_per_box: Number(f.strips_per_box || 0), tablets_per_strip: Number(f.tablets_per_strip || 0),
  });

  const toggleSupplier = (id) => setForm((f) => ({
    ...f,
    supplier_ids: (f.supplier_ids || []).includes(id)
      ? f.supplier_ids.filter((x) => x !== id)
      : [...(f.supplier_ids || []), id],
  }));

  const submit = async (e) => {
    e.preventDefault(); setFormError("");
    try {
      if (editing) {
        await api.patch(`/pharmacy/medicines/${editing}`, numeric(form));
        toast.success("Medicine updated.");
      } else {
        await api.post("/pharmacy/medicines", numeric(form));
        toast.success("Medicine added to the master.");
      }
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { setFormError(apiError(err)); }
  };

  const edit = (m) => {
    setEditing(m.medicine_id);
    setForm({ ...EMPTY, ...m, mrp: String(m.mrp), purchase_price: String(m.purchase_price),
      selling_price: String(m.selling_price), gst_percent: String(m.gst_percent),
      low_stock_limit: String(m.low_stock_limit), supplier_ids: m.supplier_ids || [],
      strips_per_box: String(m.strips_per_box || 0), tablets_per_strip: String(m.tablets_per_strip || 0) });
    setOpen(true);
  };

  return (
    <div data-testid="medicines-page">
      {!embedded && (
        <PageHeader overline="Pharmacy" title="Medicine master"
          subtitle="Every medicine carries a unique code used in POS search, plus HSN, GST and a reorder level."
          actions={can("pharmacy.medicines.create") && (
            <div className="flex gap-2">
              <ScanButton onClick={() => setScanOpen(true)} label="Scan barcode" testid="medicines-scan-btn" />
              <button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className={primaryBtnCls} data-testid="add-medicine-btn">Add medicine</button>
            </div>
          )} />
      )}
      {embedded && can("pharmacy.medicines.create") && (
        <div className="mb-6 flex gap-2">
          <ScanButton onClick={() => setScanOpen(true)} label="Scan barcode" testid="medicines-scan-btn" />
          <button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }}
            className={primaryBtnCls} data-testid="add-medicine-btn">Add medicine</button>
        </div>
      )}

      <input placeholder="Search code, name, generic, brand or barcode" value={q} data-testid="medicine-search-input"
        onChange={(e) => { setQ(e.target.value); setPage(1); }}
        className="mb-6 w-full sm:w-96 rounded-lg border border-slate-300 px-4 py-2 text-sm outline-none focus:border-slate-900" />

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <>
          <Table testid="medicines-table" columns={["Code", "Medicine", "Barcode / HSN", "MRP", "Selling", "GST", "Stock", "Limit", "Status", ""]}
            rows={items} empty={<EmptyState title="No medicines yet" subtitle="Add medicines, then record a purchase to create batches." testid="medicines-empty" />}
            renderRow={(m) => {
              if (m._is_master) {
                // master-only entry
                return (
                  <tr key={m.master_id} data-testid={`medicine-row-master-${m.master_id}`}>
                    <Td className="font-mono-num font-semibold text-slate-900">{m.code || "—"}</Td>
                    <Td className="font-medium text-slate-900">
                      {m.name}
                      <span className="block text-xs text-slate-400">{m.generic_name || m.brand || "Master product"} · {m.dosage_form || ""}</span>
                      <span className="block text-xs text-slate-500">MASTER PRODUCT</span>
                    </Td>
                    <Td className="text-xs text-slate-500">{m.barcode || "—"}<span className="block">{m.hsn || "—"}</span></Td>
                    <Td className="text-slate-600">{inr(m.mrp)}</Td>
                    <Td className="text-slate-900">{inr(m.selling_price)}</Td>
                    <Td className="text-slate-500">{m.gst_percent}%</Td>
                    <Td className="text-slate-900">—</Td>
                    <Td className="text-slate-500">—</Td>
                    <Td><StatusPill status="active" /></Td>
                    <Td className="text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={async () => {
                          try {
                            await api.post('/pharmacy/medicines/import-master', { master_id: m.master_id });
                            toast.success('Product added to your inventory (zero stock).');
                            load();
                          } catch (err) { toast.error(apiError(err) || 'Could not add product.'); }
                        }} className={primaryBtnCls} data-testid={`add-master-${m.master_id}`}>
                          Add to inventory
                        </button>
                      </div>
                    </Td>
                  </tr>
                );
              }
              // tenant medicine row
              return (
                <tr key={m.medicine_id} data-testid={`medicine-row-${m.medicine_id}`}>
                  <Td className="font-mono-num font-semibold text-slate-900">{m.code || "—"}</Td>
                  <Td className="font-medium text-slate-900">{m.name}<span className="block text-xs text-slate-400">{m.generic_name || m.brand || "—"} · {m.dosage_form}</span></Td>
                  <Td className="text-xs text-slate-500">{m.barcode || "—"}<span className="block">{m.hsn || "—"}</span></Td>
                  <Td className="text-slate-600">{inr(m.mrp)}</Td>
                  <Td className="text-slate-900">{inr(m.selling_price)}</Td>
                  <Td className="text-slate-500">{m.gst_percent}%</Td>
                  <Td className={m.low_stock ? "text-amber-700 font-semibold" : "text-slate-900"}>{m.stock ?? 0}</Td>
                  <Td className="text-slate-500">{m.low_stock_limit}</Td>
                  <Td>{m.status === "inactive" ? <StatusPill status="expired" />
                    : m.low_stock ? <StatusPill status="expiring" /> : <StatusPill status="active" />}</Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setDetailId(m.medicine_id)} className={ghostBtnCls}
                        data-testid={`view-medicine-${m.medicine_id}`}>View</button>
                      {can("pharmacy.medicines.edit") && (
                        <button onClick={() => edit(m)} className={ghostBtnCls} data-testid={`edit-medicine-${m.medicine_id}`}>Edit</button>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            }} />
          <div className="mt-5 flex items-center justify-between text-sm text-slate-500">
            <span>{total} medicine(s)</span>
            <div className="flex gap-3">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className={ghostBtnCls} data-testid="medicines-prev-btn">Previous</button>
              <button disabled={page * 25 >= total} onClick={() => setPage((p) => p + 1)} className={ghostBtnCls} data-testid="medicines-next-btn">Next</button>
            </div>
          </div>
        </>
      )}

      <Modal open={open} onClose={() => { setOpen(false); setEditing(null); }} wide
        title={editing ? "Edit medicine" : "Add medicine"} testid="medicine-modal">
        <form onSubmit={submit} className="grid gap-5 sm:grid-cols-3">
          <Field label="Medicine code *">
            <div className="flex gap-2">
              <input required minLength={2} data-testid="medicine-code-input" className={inputCls}
                value={form.code} placeholder="e.g. DO650"
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
              <button type="button" className={ghostBtnCls} data-testid="medicine-code-suggest-btn"
                onClick={async () => {
                  if (!form.name.trim()) { toast.error("Enter the medicine name first."); return; }
                  try {
                    const { data } = await api.get("/pharmacy/medicines/code-suggestion",
                      { params: { name: form.name.trim() } });
                    setForm((f) => ({ ...f, code: data.code }));
                  } catch (err) { toast.error(apiError(err)); }
                }}>Suggest</button>
            </div>
          </Field>
          <Field label="Medicine name *"><input required data-testid="medicine-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Generic name"><input className={inputCls} value={form.generic_name} onChange={(e) => setForm({ ...form, generic_name: e.target.value })} /></Field>
          <Field label="Brand"><input className={inputCls} value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></Field>
          <Field label="Category"><input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
          <Field label="Dosage form">
            <select className={inputCls} value={form.dosage_form} onChange={(e) => setForm({ ...form, dosage_form: e.target.value })}>
              {["tablet", "capsule", "syrup", "injection", "ointment", "drops", "other"].map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Unit">
            <select className={inputCls} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
              {["strip", "bottle", "vial", "tube", "box", "piece"].map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Box contains (strips)">
            <input type="number" min="0" className={inputCls} data-testid="medicine-strips-per-box"
              value={form.strips_per_box} onChange={(e) => setForm({ ...form, strips_per_box: e.target.value })} />
          </Field>
          <Field label="Strip contains (tablets)">
            <input type="number" min="0" className={inputCls} data-testid="medicine-tablets-per-strip"
              value={form.tablets_per_strip} onChange={(e) => setForm({ ...form, tablets_per_strip: e.target.value })} />
          </Field>
          <Field label="Barcode"><input className={inputCls} data-testid="medicine-barcode-input" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} /></Field>
          <Field label="HSN"><input className={inputCls} value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} /></Field>
          <Field label="Rack / location"><input className={inputCls} value={form.rack} onChange={(e) => setForm({ ...form, rack: e.target.value })} /></Field>
          <Field label="MRP (₹)"><input type="number" data-testid="medicine-mrp-input" className={inputCls} value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} /></Field>
          <Field label="Purchase price (₹)"><input type="number" className={inputCls} value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} /></Field>
          <Field label="Selling price (₹)"><input type="number" data-testid="medicine-selling-input" className={inputCls} value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} /></Field>
          <Field label="GST %"><input type="number" className={inputCls} value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} /></Field>
          <Field label="Low stock limit *"><input type="number" data-testid="medicine-low-stock-input" className={inputCls} value={form.low_stock_limit} onChange={(e) => setForm({ ...form, low_stock_limit: e.target.value })} /></Field>
          <div className="sm:col-span-3">
            <p className="text-[12px] font-semibold uppercase tracking-wider text-slate-700">Suppliers for this product</p>
            <p className="mt-1 text-xs text-slate-500">A product can be supplied by several suppliers. Links are also created automatically when you purchase it.</p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="medicine-supplier-links">
              {suppliers.length === 0 && <span className="text-sm text-slate-400">No active suppliers yet.</span>}
              {suppliers.map((s) => {
                const on = (form.supplier_ids || []).includes(s.supplier_id);
                return (
                  <button type="button" key={s.supplier_id} onClick={() => toggleSupplier(s.supplier_id)}
                    data-testid={`medicine-supplier-${s.supplier_id}`}
                    className={`rounded-full px-4 py-2 text-sm font-medium ${on
                      ? "bg-[#0F172A] text-white" : "border border-slate-300 text-slate-600 hover:border-slate-900"}`}>
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>
          {formError && <p className="sm:col-span-3 text-sm text-red-600" data-testid="medicine-form-error">{formError}</p>}
          <div className="sm:col-span-3"><button className={primaryBtnCls} data-testid="medicine-submit-btn">{editing ? "Update medicine" : "Save medicine"}</button></div>
        </form>
      </Modal>

      <MedicineDetail medicineId={detailId} onClose={() => setDetailId(null)} onChanged={load}
        onEdit={(m) => { setDetailId(null); edit(m); }} />

      {scanOpen && (
        <BarcodeScanner testid="medicines-scanner"
          onScan={(code) => handleScan(code)}
          onClose={() => setScanOpen(false)} />
      )}
    </div>
  );
}
